import { useGetPatientDiagnosesQuery } from '~/store/services/diagnoses';
import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  Tag,
  TagColor,
  Text,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';
import { Path } from '~/routes/constants';
import { DiagnosisStatusCode } from '~/lib/util/clinicalProfile.util';
import { TreatmentOverviewCardSecondaryDiagnosesList } from './TreatmentOverviewCardSecondaryDiagnosesList';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

const TOP_LEVEL_ID = 'patient-overview-diagnosis';

const DIAGNOSIS_SUBSECTION_WIDTH = '316px';

type Props = {
  patientId: string;
};

export const TreatmentOverviewCardDiagnosisSection = ({ patientId }: Props) => {
  const { trackEvent } = useAnalytics();
  const { data: patientDiagnoses } = useGetPatientDiagnosesQuery(patientId);
  const history = useHistory();

  const activePrimaryDiagnosis = patientDiagnoses?.find(
    (diagnosis) => diagnosis.active && diagnosis.primary,
  );

  const activeSecondaryDiagnoses = patientDiagnoses?.filter(
    (d) => d.active && !d.primary,
  );

  return (
    <>
      {!activePrimaryDiagnosis && (
        <ChakraFlex
          alignItems="center"
          flexDirection="column"
          gap="16px"
          width={DIAGNOSIS_SUBSECTION_WIDTH}
        >
          <Text color="neutral.secondary" textAlign="center" variant="caption">
            A primary diagnosis is required for the care plan.
          </Text>
          <ChakraBox>
            <FireflyButton
              id="treatment-attributes-go-to-clinical-profile"
              leftIcon={FAIconName.externalLinkRegular}
              onClick={() => {
                trackEvent(CarePlanEvents.clickOnGoToClinicalProfileButton);
                history.push(
                  `${Path.MAUD_PATIENTS}/${patientId}/clinical-profile`,
                );
              }}
              size="small"
              variant="secondary"
            >
              Go to clinical profile
            </FireflyButton>
          </ChakraBox>
        </ChakraFlex>
      )}
      {activePrimaryDiagnosis && (
        <ChakraFlex
          borderColor="neutral.line"
          borderRadius="12px"
          borderWidth="1px"
          flexDirection="column"
          gap="12px"
          marginBottom="21px"
          padding="18px"
          width={DIAGNOSIS_SUBSECTION_WIDTH}
        >
          <ChakraFlex justifyContent="space-between">
            <ChakraFlex alignItems="center">
              <Tag
                color={TagColor.periwinkle}
                id={`${TOP_LEVEL_ID}-primary`}
                value="Primary"
              />
              <Text color="neutral.secondary" marginLeft="12px" variant="body">
                {activePrimaryDiagnosis.diagnosis.code}
              </Text>
            </ChakraFlex>
            <ChakraBox>
              <Tag
                color={
                  activePrimaryDiagnosis.statusCode ===
                  DiagnosisStatusCode.PRELIMINARY
                    ? TagColor.marigold
                    : TagColor.green
                }
                id={`${TOP_LEVEL_ID}-status`}
                value={activePrimaryDiagnosis.statusLabel}
              />
            </ChakraBox>
          </ChakraFlex>

          <Text variant="bodyMedium">
            {activePrimaryDiagnosis.diagnosis.shortDescription}
          </Text>

          <TreatmentOverviewCardSecondaryDiagnosesList
            secondaryDiagnoses={activeSecondaryDiagnoses}
          />
        </ChakraFlex>
      )}

      {!activePrimaryDiagnosis && activeSecondaryDiagnoses?.length > 0 && (
        <ChakraBox marginTop="18px" width={DIAGNOSIS_SUBSECTION_WIDTH}>
          <TreatmentOverviewCardSecondaryDiagnosesList
            secondaryDiagnoses={activeSecondaryDiagnoses}
          />
        </ChakraBox>
      )}
    </>
  );
};
